<template>
  <q-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <q-card
      ref="detailCard"
      class="usp-work-detail-card"
      :style="cardPositionStyle"
    >
      <div
        class="bg-primary text-white usp-work-detail-card__bar"
        @mousedown="onDragStart"
      >
        <div class="usp-work-detail-card__bar-row">
          <q-icon name="work" size="18px" class="usp-work-detail-card__icon" />
          <q-space />
          <q-btn icon="close" flat dense no-caps size="md" label="Закрыть" v-close-popup />
        </div>
        <div class="usp-work-detail-card__title">{{ data.workName }}</div>
      </div>

      <div v-if="data.loading" class="usp-work-detail-card__loading">
        <q-spinner size="30px" color="primary" />
      </div>

      <div v-else class="usp-work-detail-card__body">
        <div v-if="data.workGuid" class="q-px-sm q-pt-sm">
          <div class="text-caption text-grey-6">гуид: <span class="text-grey-8">{{ data.workGuid }}</span></div>
        </div>

        <div class="q-pa-sm q-mt-sm text-subtitle2" style="color:#b45309">работа</div>
        <q-markup-table dense flat separator="cell" class="q-mb-md">
          <thead>
            <tr style="background:#fff3e0">
              <th class="text-left">дата факта</th>
              <th class="text-right">объём факт</th>
              <th class="text-right">цена</th>
              <th class="text-right">сумма</th>
              <th class="text-center">статус</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="f in data.facts" :key="f.value_date">
              <td class="text-left text-caption">{{ fmtDateRu(f.value_date) }}</td>
              <td class="text-right mono">{{ fmtNum(f.fact_value) }}</td>
              <td class="text-right mono">{{ fmtNum(f.work_set_price) }}</td>
              <td class="text-right mono">{{ fmtNum(f.fact_price) }}</td>
              <td class="text-center">
                <span :class="f.is_sent ? 'text-green-7' : 'text-orange-7'" class="text-caption text-weight-medium">
                  {{ f.is_sent ? 'отправлен' : 'не отправлен' }}
                </span>
              </td>
            </tr>
            <tr v-if="!data.facts.length">
              <td colspan="5" class="text-center text-grey-5">Нет данных</td>
            </tr>
          </tbody>
        </q-markup-table>

        <template v-for="(group, gi) in materialGroups" :key="gi">
          <div class="q-pa-sm text-subtitle2" style="color:#1565c0">
            {{ group.name }}<span v-if="group.unit" class="text-caption text-grey-6">/{{ group.unit }}</span>
          </div>
          <q-markup-table dense flat separator="cell" class="q-mb-md">
            <thead>
              <tr style="background:#e3f2fd">
                <th class="text-left">дата факта</th>
                <th class="text-right">кол-во</th>
                <th class="text-right">цена</th>
                <th class="text-right">сумма</th>
                <th class="text-center">статус</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in group.rows" :key="m.value_date">
                <td class="text-left text-caption">{{ fmtDateRu(m.value_date) }}</td>
                <td class="text-right mono">{{ fmtNum(m.fact_value) }}</td>
                <td class="text-right mono">{{ fmtNum(m.work_set_price) }}</td>
                <td class="text-right mono">{{ fmtNum(m.fact_price) }}</td>
                <td class="text-center">
                  <span :class="m.is_sent ? 'text-green-7' : 'text-orange-7'" class="text-caption text-weight-medium">
                    {{ m.is_sent ? 'отправлен' : 'не отправлен' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </template>
        <div v-if="!materialGroups.length && data.facts.length" class="q-pa-sm text-grey-5 text-caption">
          Материалы отсутствуют
        </div>
      </div>
    </q-card>
  </q-dialog>
</template>

<script>
import { useFormatters } from '../composables/useFormatters';

const CARD_WIDTH = 640;

export default {
  name: 'PpmUspWorkDetailDialog',
  props: {
    modelValue: Boolean,
    data: { type: Object, default: () => ({ show: false, loading: false, workName: '', workGuid: '', facts: [], materials: [] }) },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      posX: null,
      posY: null,
      isDragging: false,
      dragOffsetX: 0,
      dragOffsetY: 0,
    };
  },
  setup() {
    const { fmtNum, fmtDateRu } = useFormatters();
    return { fmtNum, fmtDateRu };
  },
  computed: {
    materialGroups() {
      const mats = this.data.materials || [];
      const map = new Map();
      for (const m of mats) {
        const key = m.material_name || '—';
        if (!map.has(key)) map.set(key, { name: key, unit: m.unit_name || '', rows: [] });
        map.get(key).rows.push(m);
      }
      return [...map.values()];
    },
    cardPositionStyle() {
      const x = this.posX ?? Math.max(16, window.innerWidth - CARD_WIDTH - 16);
      const y = this.posY ?? 48;
      return {
        position: 'fixed',
        left: `${x}px`,
        top: `${y}px`,
        margin: 0,
        width: `${CARD_WIDTH}px`,
        maxWidth: '92vw',
        maxHeight: 'calc(100vh - 32px)',
      };
    },
  },
  watch: {
    modelValue(val) {
      if (val) {
        this.posX = null;
        this.posY = null;
        this.$nextTick(() => this.initPosition());
      } else {
        this.stopDrag();
      }
    },
    'data.loading'(loading) {
      if (!loading && this.modelValue) {
        this.$nextTick(() => this.clampPosition());
      }
    },
  },
  beforeUnmount() {
    this.stopDrag();
  },
  methods: {
    getCardElement() {
      const ref = this.$refs.detailCard;
      return ref?.$el || ref;
    },
    initPosition() {
      const card = this.getCardElement();
      const cardWidth = card?.offsetWidth || CARD_WIDTH;
      const cardHeight = card?.offsetHeight || 320;
      this.posX = Math.max(16, window.innerWidth - cardWidth - 16);
      this.posY = Math.max(16, Math.min(window.innerHeight - cardHeight - 16, 48));
    },
    clampPosition() {
      const card = this.getCardElement();
      if (!card) return;
      const cardWidth = card.offsetWidth || CARD_WIDTH;
      const cardHeight = card.offsetHeight;
      const x = this.posX ?? Math.max(16, window.innerWidth - cardWidth - 16);
      const y = this.posY ?? 48;
      this.posX = Math.min(Math.max(0, x), Math.max(0, window.innerWidth - cardWidth));
      this.posY = Math.min(Math.max(0, y), Math.max(0, window.innerHeight - cardHeight));
    },
    onDragStart(event) {
      if (event.button !== 0) return;
      if (event.target.closest('button')) return;

      const card = this.getCardElement();
      if (!card) return;

      const rect = card.getBoundingClientRect();
      this.posX = rect.left;
      this.posY = rect.top;
      this.isDragging = true;
      this.dragOffsetX = event.clientX - this.posX;
      this.dragOffsetY = event.clientY - this.posY;

      document.addEventListener('mousemove', this.onDragMove);
      document.addEventListener('mouseup', this.onDragEnd);
    },
    onDragMove(event) {
      if (!this.isDragging) return;

      const card = this.getCardElement();
      const cardWidth = card?.offsetWidth || CARD_WIDTH;
      const cardHeight = card?.offsetHeight || 320;
      const maxX = Math.max(0, window.innerWidth - cardWidth);
      const maxY = Math.max(0, window.innerHeight - cardHeight);

      this.posX = Math.min(maxX, Math.max(0, event.clientX - this.dragOffsetX));
      this.posY = Math.min(maxY, Math.max(0, event.clientY - this.dragOffsetY));
    },
    onDragEnd() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDragMove);
      document.removeEventListener('mouseup', this.onDragEnd);
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDragMove);
      document.removeEventListener('mouseup', this.onDragEnd);
    },
  },
};
</script>

<style scoped lang="scss">
.usp-work-detail-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.usp-work-detail-card__bar {
  cursor: move;
  user-select: none;
  flex-shrink: 0;
  padding: 8px 12px 10px;
}

.usp-work-detail-card__bar-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 28px;
}

.usp-work-detail-card__icon {
  flex-shrink: 0;
}

.usp-work-detail-card__title {
  margin-top: 4px;
  font-size: 13px;
  line-height: 1.45;
  font-weight: 500;
  white-space: normal;
  word-break: break-word;
}

.usp-work-detail-card__loading {
  padding: 32px;
  text-align: center;
}

.usp-work-detail-card__body {
  overflow-y: auto;
  max-height: calc(100vh - 96px);
}
</style>
